/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../buttons/Button";
import { errorAlert, makeApiRequest, successAlert } from "../../util";
import AuthContext from "../../store/authContext";
import { ADD_CANNED_MESSAGES_FORM_FIELDS } from "../../constants";
import Form from "../../ui/Form/Form";
import NumberField from "../inputs/NumberField";
import TextAreaField from "../inputs/TextAreaField";
import { ICannedMessageData } from "../../types";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import {
  CREATE_CANNED_MESSAGE_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
} from "../../translations/util";

interface IProps {
  org: string;
}
const set = new Set();

const isInValidNumberForField = (val?: number, min?: number, max?: number) => {
  if (val || val === 0) {
    return val < (min || 0) || val > (max || 1048575);
  }

  return true;
};

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;

  const isConnectionFormInvalid = ADD_CANNED_MESSAGES_FORM_FIELDS.map(
    (rows: any) => {
      const res = rows.fields.map((field: any) => {
        switch (field.type) {
          case "TEXTAREA":
            return field.isInputInvalid?.(data[field.name]);
          case "NUMBER":
            return isInValidNumberForField(
              data[field.name],
              field.min,
              field.max
            );
          default:
            return false;
        }
      });

      return res.reduce((acc: any, el: any) => acc || el);
    }
  ).reduce((acc: any, el: any) => acc || el);

  return isConnectionFormInvalid;
};

const NewCannedMessage = ({ org }: IProps) => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);

  const [connectionData, setConnectionData] = useState<ICannedMessageData>({
    "MSG-ID": undefined,
    "CANNED-TEXT": undefined,
  });

  const createCannedMessage = async () => {
    setLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/lmr-msgs`,
        method: "POST",
        body: JSON.stringify({
          "MSG-ID": String(connectionData["MSG-ID"]),
          "CANNED-TEXT": connectionData["CANNED-TEXT"],
        }),
        token,
        org,
      });
      successAlert(CREATE_CANNED_MESSAGE_TRANSLATIONS.successAlertMessage);
      navigate("/configurationManagement/cannedMessages");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="data-form">
        <Form>
          <div className="data-tabcontent data-card">
            {ADD_CANNED_MESSAGES_FORM_FIELDS.map(
              (rows: {
                cols: number;
                fields: {
                  name: keyof ICannedMessageData;
                  tooltipText?: string;
                  label: string;
                  type: string;
                  invalidText?: string | null;
                  isInputInvalid?: (val?: string) => boolean;
                  min?: number;
                  max?: number;
                }[];
              }) => {
                return (
                  <div
                    className={`grid grid-cols-${rows.cols} gap-4 form-control single-item`}
                  >
                    {rows.fields.map((field) => {
                      switch (field.type) {
                        case "NUMBER":
                          return (
                            <NumberField
                              onChange={(val) => {
                                set.add(field.name);
                                if (!val || !Number.isNaN(+val))
                                  setConnectionData({
                                    ...connectionData,
                                    [field.name]: val ? +val : undefined,
                                  });
                              }}
                              label={field.label}
                              placeholder={field.label}
                              max={field.max || 0}
                              min={field.min || 0}
                              invalid={
                                set.has(field.name) &&
                                connectionData[field.name] !== 0 &&
                                !connectionData[field.name]
                              }
                              value={connectionData[field.name] as number}
                              invalidText={`"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`}
                              tooltipText={field.tooltipText}
                              required
                            />
                          );
                        default:
                          return (
                            <TextAreaField
                              onChange={(val) => {
                                set.add(field.name);
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                });
                              }}
                              placeholder={field.label}
                              value={connectionData[field.name] as string}
                              invalid={
                                set.has(field.name) &&
                                field.isInputInvalid?.(
                                  connectionData[field.name] as string
                                )
                              }
                              invalidText={
                                field.invalidText ||
                                FORM_VALIDATION_TRANSLATIONS.required(
                                  field.label
                                )
                              }
                              labelText={field.label}
                              tooltipText={field.tooltipText}
                              required
                            />
                          );
                      }
                    })}
                  </div>
                );
              }
            )}
          </div>
        </Form>
      </div>
      <DeleteModal
        modalHeaderText={CREATE_CANNED_MESSAGE_TRANSLATIONS.deleteModal.header}
        modalDescriptionText={
          CREATE_CANNED_MESSAGE_TRANSLATIONS.deleteModal.description
        }
        onDelete={() => navigate("/configurationManagement/cannedMessages")}
        open={open}
        setOpen={setOpen}
        confirmationButtonLabel={
          CREATE_CANNED_MESSAGE_TRANSLATIONS.deleteModal.goBackButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <div className="flex justify-end form-footer">
        <div className="mr-2">
          <Button
            className="w-full"
            type="button"
            kind="tertiary"
            disabled={false}
            label={CREATE_CANNED_MESSAGE_TRANSLATIONS.cancelButtonLabel}
            onClick={() => setOpen(true)}
            dataCy="cancel-"
          />
        </div>

        <div>
          <Button
            className="w-full"
            type="button"
            loading={loading}
            disabled={isFormInvalid({ data: connectionData })}
            onClick={createCannedMessage}
            label={CREATE_CANNED_MESSAGE_TRANSLATIONS.createButtonLabel}
            dataCy="createCannedMessage-"
          />
        </div>
      </div>
    </>
  );
};

export default NewCannedMessage;
